import type { CopilotProvider } from "../types";

/**
 * Provider para Ollama (compatible local y cloud).
 * Cloud usa el mismo endpoint /api/chat con un Authorization header.
 *
 * Tool calling soportado por modelos: llama3.1, llama3.2, qwen2.5,
 * mistral-nemo, command-r-plus, etc.
 */
function makeOllama(
  id: "ollama-local" | "ollama-cloud",
  label: string,
  defaultBaseUrl: string,
  defaultModel: string,
): CopilotProvider {
  return {
    id,
    label,
    defaultBaseUrl,
    defaultModel,

    async listModels({ baseUrl, apiKey }) {
      try {
        const res = await fetch(`${baseUrl.replace(/\/$/, "")}/api/tags`, {
          headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : {},
        });
        if (!res.ok) return [];
        const json = (await res.json()) as { models?: { name: string }[] };
        return (json.models ?? []).map((m) => m.name);
      } catch {
        return [];
      }
    },

    async chat({
      baseUrl,
      apiKey,
      model,
      systemPrompt,
      messages,
      tools,
      signal,
    }) {
      const url = `${baseUrl.replace(/\/$/, "")}/api/chat`;
      const body: Record<string, unknown> = {
        model,
        stream: false,
        messages: [
          { role: "system", content: systemPrompt },
          ...messages.map((m) => {
            const base: Record<string, unknown> = {
              role: m.role,
              content: m.content,
            };
            if (m.tool_calls) base.tool_calls = m.tool_calls;
            if (m.tool_call_id) base.tool_call_id = m.tool_call_id;
            if (m.name) base.name = m.name;
            return base;
          }),
        ],
        options: { temperature: 0.3 },
      };
      if (tools && tools.length > 0) body.tools = tools;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
        },
        body: JSON.stringify(body),
        signal,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Ollama (${res.status}): ${text || res.statusText || "fallo desconocido"}`,
        );
      }
      const json = (await res.json()) as {
        message?: {
          content?: string;
          tool_calls?: Array<{
            id?: string;
            function: {
              name: string;
              arguments: Record<string, unknown> | string;
            };
          }>;
        };
      };

      const content = (json.message?.content ?? "").trim();
      const rawCalls = json.message?.tool_calls ?? [];

      const toolCalls = rawCalls.map((tc, idx) => {
        let args: Record<string, unknown> = {};
        try {
          args =
            typeof tc.function.arguments === "string"
              ? (JSON.parse(tc.function.arguments) as Record<string, unknown>)
              : tc.function.arguments;
        } catch {
          args = {};
        }
        return {
          id: tc.id ?? `tc-${Date.now()}-${idx}`,
          name: tc.function.name,
          arguments: args,
        };
      });

      return { content, toolCalls };
    },
  };
}

export const ollamaLocal = makeOllama(
  "ollama-local",
  "Ollama (local)",
  "http://localhost:11434",
  "llama3.1",
);

export const ollamaCloud = makeOllama(
  "ollama-cloud",
  "Ollama Cloud",
  "https://ollama.com",
  "gpt-oss:120b",
);
